/* Service Worker registration helper.
 * Registers on both dev and production so that Web Push notifications work in
 * the preview environment (which is served in dev mode).
 *
 * iter46 — Also detects when a NEW SW has installed in the background and
 * dispatches a `sw:update-available` window event so the UI can prompt the
 * user to reload (see NewVersionBanner). Ends the "hard-refresh manually
 * whenever you deploy" era.
 */
const emitUpdateAvailable = (worker) => {
  window.dispatchEvent(new CustomEvent('sw:update-available', {
    detail: { worker },
  }));
};

export const registerServiceWorker = () => {
  if (!('serviceWorker' in navigator)) return;
  if (!window.isSecureContext) return; // Push requires https / localhost
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((reg) => {
        // Force an update-check so newly-shipped SW versions activate quickly
        // on the next visit rather than waiting for the 24h browser refresh.
        try { reg.update(); } catch (_) { /* ignore */ }

        // If a waiting worker is already present at registration time, prompt.
        if (reg.waiting && navigator.serviceWorker.controller) {
          emitUpdateAvailable(reg.waiting);
        }

        // Fired when a new SW version is being installed
        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // 'installed' + existing controller = a new SW is waiting to take
            // over. Prompt the user to reload.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              emitUpdateAvailable(installing);
            }
          });
        });
      })
      .catch((e) => console.warn('[SW] register failed', e));

    // When the new SW activates (after the user clicks "Reload"), the
    // controllerchange event fires — reload the page to pick up fresh assets.
    let hasReloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (hasReloaded) return;
      hasReloaded = true;
      window.location.reload();
    });
  });
};
